import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import ThemeToggle from "@/components/ThemeToggle";

/**
 * Generic login shell for admin/superadmin (username + password + access code).
 * Users have their own dedicated component (UserLogin) because of the 2-step brand code flow.
 */
export default function StaffLogin({ role, title, subtitle, loginRoute }) {
  const [form, setForm] = useState({ username: "", password: "", code: "" });
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();
  const { login } = useAuth();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", {
        username: form.username, password: form.password, role, code: form.code,
      });
      login(data.token, data.user);
      toast.success("Signed in");
      nav(role === "superadmin" ? "/superadmin" : "/admin");
    } catch (err) {
      toast.error(formatError(err));
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col">
      <header className="border-b border-border">
        <div className="max-w-5xl mx-auto px-6 h-14 flex items-center justify-end">
          <ThemeToggle />
        </div>
      </header>
      <main className="flex-1 grid place-items-center px-4 py-10">
        <Card className="w-full max-w-md border-border">
          <CardHeader>
            <CardTitle className="font-display text-2xl">{title}</CardTitle>
            <CardDescription>{subtitle}</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={submit} className="space-y-4" data-testid={`${role}-login-form`}>
              <div>
                <Label htmlFor="username">Username</Label>
                <Input id="username" data-testid="username-input" required value={form.username}
                  onChange={(e)=>setForm({...form, username:e.target.value})} />
              </div>
              <div>
                <Label htmlFor="password">Password</Label>
                <Input id="password" type="password" data-testid="password-input" required value={form.password}
                  onChange={(e)=>setForm({...form, password:e.target.value})} />
              </div>
              <div>
                <Label htmlFor="code">Access code</Label>
                <Input id="code" data-testid="code-input" required value={form.code}
                  onChange={(e)=>setForm({...form, code:e.target.value.toUpperCase()})}
                  className="mono tracking-widest" placeholder="ABCD1234" />
              </div>
              <Button type="submit" className="w-full btn-push" data-testid="login-submit-btn" disabled={loading}>
                {loading ? "Signing in…" : "Sign in"}
              </Button>
              <div className="text-xs text-muted-foreground text-center">
                <Link to="/" className="hover:underline">Back to home</Link>
              </div>
            </form>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
